import React, {Fragment} from "react";
import classnames from "classnames";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useLocation} from "react-router-dom";
import humanizeString from "string-humanize";
import useBreadcrumbs from "use-react-router-breadcrumbs";
import {updateSelectedLocation} from "../../../../redux/Actions/sites.actions";
import {getSelectedController} from "../../../../redux/Selectors/controllers.selectors";
import {getCurrentCustomer} from "../../../../redux/Selectors/customers.selectors";
import {
  getCurrentSite,
  getSelectedLocations,
} from "../../../../redux/Selectors/sites.selectors";
import {appRoutes} from "../../../../routes";
import "./breadcrumb.scss";
import BreadcrumbItem from "./breadcrumbItem";

const Breadcrumb = () => {
  const selectedCustomer = useSelector(getCurrentCustomer);
  const selectedSite = useSelector(getCurrentSite);
  const selectedController = useSelector(getSelectedController);
  const selectedLocation = useSelector(getSelectedLocations);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const url = location.pathname;
  const params = url.split("/");

  const breadCrumbRoutes = appRoutes
    .filter((route) => route.path)
    .map((validRoute) => ({
      path: `/app${validRoute.path}`,
    }));

  const breadcrumbs = useBreadcrumbs([...breadCrumbRoutes], {
    excludePaths: ["/", "/app"],
    disableDefaults: false,
  });

  const navigateToCustomer = () => {
    navigate("/app/customer");
  };

  const navigateToSite = () => {
    navigate(`/app/sites/${selectedSite.id}`);
  };

  const navigateToController = (tab) => {
    navigate(`/app/appliance/${selectedController.mac}`, {
      state: {
        activeTab: tab,
      },
    });
  };

  const handleClick = (event, isLastElement, index, match) => {
    if (isLastElement) {
      event.preventDefault();
    } else if (params[2] === "appliance") {
      if (index === 0) {
        {
          /* /app/appliance/:controllerId will take you to /app/site/:siteId with the location unset */
        }
        dispatch(updateSelectedLocation());
        navigateToSite();
      } else if (index === 1) {
        navigateToController();
      } else if (index === 2 && params[4] === "snapshots") {
        navigateToController("snapshot");
      }
    } else if (index === 0) {
      if (params[2] === "system-alert-history") navigate("/app/system-alerts");
      else if (params[2] === "sensor-alert-history") navigate("/app/sensor-alerts");
      else if (params[2] === "webhook-delivery-logs") navigate("/app/webhooks");
      else if (params[2] === "sites" && selectedLocation?.name)
        dispatch(updateSelectedLocation());
      else if (params[2] === "reports") {
        const isReportPage =
          params[3]?.includes("report") ||
          params[4]?.includes("report") ||
          params[4]?.includes("charts");

        navigate("/app/reports", {
          state: {
            activeTab: isReportPage ? "2" : "1",
          },
        });
      }
    } else {
      navigate(match.url);
    }
  };

  const getBreadcrumbText = (index, breadcrumb) => {
    if (index === 0) {
      if (params[2] === "sites") {
        return selectedSite?.name;
      } else if (params[2] === "appliance") {
        return selectedSite?.name;
      } else if (params[2] === "system-alert-history" || params[2] === "system-alerts") {
        return "System Alerts";
      } else if (params[2] === "sensor-alert-history" || params[2] === "sensor-alerts") {
        return "Sensor Alerts";
      } else if (params[2] === "webhook-delivery-logs") {
        return "Webhooks";
      } else {
        return humanizeString(breadcrumb?.props?.children);
      }
    } else if (index === 1) {
      if (params[2] === "sites") {
        return selectedLocation?.name;
      } else if (params[2] === "appliance") {
        return selectedController?.name;
      } else {
        return humanizeString(breadcrumb?.props?.children);
      }
    }
    return humanizeString(breadcrumb?.props?.children);
  };

  const getBreadcrumbTextClassNames = (isLastElement, index) => {
    return classnames(
      {
        "active--breadcrumb":
          isLastElement ||
          (index === 2 && params[4] === "sensor") ||
          (index === 1 && params[2] === "reports") ||
          (index === 0 && params[2] === "sites" && !selectedLocation?.name),
      },
      {
        "d-none": index === 2 && params[2] === "reports",
      }
    );
  };

  const getRightIconClassNames = (isLastElement, index) => {
    return classnames({
      "d-none":
        isLastElement ||
        (index === 0 && params[2] === "sites" && !selectedLocation?.name) ||
        (index === 1 && params[2] === "reports"),
    });
  };

  return (
    <div>
      {selectedCustomer && (
        <BreadcrumbItem
          breadcrumbText={selectedCustomer.name}
          onClick={navigateToCustomer}
        />
      )}
      {breadcrumbs.slice(0, 4).map(({match, breadcrumb}, index) => {
        const isLastElement = breadcrumbs.length - 1 === index;
        return (
          <Fragment key={index + match.pathname}>
            {params[2] === "appliance" && index === 1 && (
              <BreadcrumbItem
                breadcrumbText={selectedLocation.name}
                onClick={navigateToSite}
              />
            )}
            <BreadcrumbItem
              breadcrumbClasses={getBreadcrumbTextClassNames(isLastElement, index)}
              breadcrumbText={getBreadcrumbText(index, breadcrumb)}
              iconClasses={getRightIconClassNames(isLastElement, index)}
              onClick={(e) => handleClick(e, isLastElement, index, match)}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumb;
